import React, { useState, useEffect } from 'react';
import { getThresholds, updateThresholds } from '../api';
import {
  Sliders, X, FloppyDisk, SpinnerGap, CheckCircle,
  ArrowUp, ArrowDown
} from '@phosphor-icons/react';

const KPI_LABELS = {
  energy_mwh: 'Energy (MWh)',
  tco2e: 'Carbon (tCO2e)',
  oee_pct: 'OEE (%)',
  trir: 'TRIR',
  cost_per_unit: 'Cost per Unit ($)',
  carbon_intensity: 'Carbon Intensity',
  assurance_coverage: 'Assurance Coverage (%)',
  scrap_pct: 'Scrap Rate (%)',
};

export default function ThresholdConfig({ industry, isOpen, onClose }) {
  const [thresholds, setThresholds] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const fetch = async () => {
      setLoading(true);
      setSaved(false);
      try {
        const { data } = await getThresholds(industry);
        setThresholds(data.thresholds || {});
      } catch (err) {
        console.error('Failed to load thresholds:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [industry, isOpen]);

  const handleChange = (metric, field, value) => {
    setThresholds(prev => ({
      ...prev,
      [metric]: { ...prev[metric], [field]: parseFloat(value) || 0 },
    }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateThresholds(industry, thresholds);
      setSaved(true);
    } catch (err) {
      console.error('Failed to save thresholds:', err);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50" onClick={onClose} />
      <div
        data-testid="threshold-config-panel"
        className="fixed inset-y-0 right-0 w-full max-w-lg bg-surface z-50 shadow-2xl flex flex-col animate-slide-in"
        style={{ animationDirection: 'normal' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-3">
            <Sliders size={20} weight="duotone" className="text-primary" />
            <div>
              <h2 className="font-heading text-lg font-bold text-text-primary">KPI Thresholds</h2>
              <p className="text-xs font-body text-text-secondary">{industry}</p>
            </div>
          </div>
          <button
            data-testid="threshold-close-button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-background transition-colors"
          >
            <X size={18} className="text-text-secondary" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="space-y-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-20 bg-background rounded-lg animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-xs font-body text-text-secondary mb-2">
                Configure target, warning, and critical levels for each KPI.
                Values outside these thresholds will trigger visual alerts on the dashboard and heat map.
              </p>

              {Object.entries(thresholds).map(([metric, config]) => {
                const isLower = config.direction === 'lower_better';
                return (
                  <div
                    key={metric}
                    data-testid={`threshold-row-${metric}`}
                    className="bg-background border border-border rounded-lg p-4"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-body text-sm font-medium text-text-primary">
                        {KPI_LABELS[metric] || metric}
                      </span>
                      <span className="flex items-center gap-1 text-[10px] font-body text-tertiary uppercase tracking-wider">
                        {isLower ? <ArrowDown size={10} /> : <ArrowUp size={10} />}
                        {isLower ? 'Lower is better' : 'Higher is better'}
                      </span>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="text-[10px] font-body font-semibold text-success uppercase tracking-wider mb-1 block">
                          Target
                        </label>
                        <input
                          data-testid={`threshold-${metric}-target`}
                          type="number"
                          step="any"
                          value={config.target}
                          onChange={(e) => handleChange(metric, 'target', e.target.value)}
                          className="w-full px-2.5 py-2 bg-surface border border-success/30 rounded-lg text-sm font-body text-text-primary focus:outline-none focus:border-success focus:ring-1 focus:ring-success"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-body font-semibold text-alert-warning uppercase tracking-wider mb-1 block">
                          Warning
                        </label>
                        <input
                          data-testid={`threshold-${metric}-warning`}
                          type="number"
                          step="any"
                          value={config.warning}
                          onChange={(e) => handleChange(metric, 'warning', e.target.value)}
                          className="w-full px-2.5 py-2 bg-surface border border-alert-warning/30 rounded-lg text-sm font-body text-text-primary focus:outline-none focus:border-alert-warning focus:ring-1 focus:ring-alert-warning"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-body font-semibold text-alert-critical uppercase tracking-wider mb-1 block">
                          Critical
                        </label>
                        <input
                          data-testid={`threshold-${metric}-critical`}
                          type="number"
                          step="any"
                          value={config.critical}
                          onChange={(e) => handleChange(metric, 'critical', e.target.value)}
                          className="w-full px-2.5 py-2 bg-surface border border-alert-critical/30 rounded-lg text-sm font-body text-text-primary focus:outline-none focus:border-alert-critical focus:ring-1 focus:ring-alert-critical"
                        />
                      </div>
                    </div>

                    {/* Visual scale */}
                    <div className="mt-2 h-1.5 rounded-full flex overflow-hidden">
                      <div className="flex-1 bg-success/30" />
                      <div className="flex-1 bg-alert-warning/30" />
                      <div className="flex-1 bg-alert-critical/30" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-border flex items-center justify-between">
          {saved && (
            <span className="flex items-center gap-1.5 text-xs font-body text-success font-medium" data-testid="threshold-saved-indicator">
              <CheckCircle size={14} weight="fill" />
              Thresholds saved
            </span>
          )}
          {!saved && <div />}
          <button
            data-testid="threshold-save-button"
            onClick={handleSave}
            disabled={saving || loading}
            className="flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-lg font-heading text-sm font-semibold hover:bg-[#2B3523] transition-all disabled:opacity-50"
          >
            {saving ? <SpinnerGap size={16} className="animate-spin" /> : <FloppyDisk size={16} />}
            {saving ? 'Saving...' : 'Save Thresholds'}
          </button>
        </div>
      </div>
    </>
  );
}
